/**
 * Sentry SDK scrubbers (ADR-037, tech-spec §12.3, acceptance S36).
 *
 * `beforeBreadcrumb` and `beforeSend` are wired into `Sentry.init` in
 * a later slice; they are kept here as pure functions so the golden
 * corpus test runs without pulling `@sentry/react`. The local types
 * mirror the SDK shape so the function signatures stay stable when
 * the SDK is added.
 *
 * Per Mission constraint #1, the cost of a false positive
 * (over-redacting a legitimate error message) is acceptable; the cost
 * of a false negative (leaking content) is not.
 */

import {
  BREADCRUMB_CATEGORY_DENY_LIST,
  CONTENT_SAFE_BREADCRUMB_FLAG,
  MARKDOWN_SHAPED_PATTERNS,
  redactionMarker,
} from './scrubPatterns'

export interface SentryExceptionValueLike {
  type?: string
  value?: string
}

export interface SentryEventLike {
  message?: string
  exception?: {
    values?: SentryExceptionValueLike[]
  }
  request?: {
    url?: string
    [key: string]: unknown
  }
  transaction?: string
}

export interface SentryBreadcrumbLike {
  category?: string
  data?: Record<string, unknown>
  [key: string]: unknown
}

export function redactMarkdownShaped(input: string): string {
  let out = input
  for (const pattern of MARKDOWN_SHAPED_PATTERNS) {
    out = out.replace(pattern, (match) => redactionMarker(match.length))
  }
  return out
}

export function stripQueryString(url: string | undefined): string | undefined {
  if (!url) return url
  const queryIndex = url.indexOf('?')
  return queryIndex >= 0 ? url.slice(0, queryIndex) : url
}

export function beforeBreadcrumb(
  breadcrumb: SentryBreadcrumbLike,
): SentryBreadcrumbLike | null {
  const category = breadcrumb.category
  if (!category || !BREADCRUMB_CATEGORY_DENY_LIST.has(category)) return breadcrumb
  if (breadcrumb.data?.[CONTENT_SAFE_BREADCRUMB_FLAG] === true) return breadcrumb
  return null
}

export function beforeSend(event: SentryEventLike): SentryEventLike {
  const next: SentryEventLike = { ...event }

  if (typeof next.message === 'string') {
    next.message = redactMarkdownShaped(next.message)
  }

  if (next.exception?.values) {
    next.exception = {
      ...next.exception,
      values: next.exception.values.map((v) => ({
        ...v,
        value: typeof v.value === 'string' ? redactMarkdownShaped(v.value) : v.value,
      })),
    }
  }

  if (next.request?.url) {
    next.request = { ...next.request, url: stripQueryString(next.request.url) }
  }

  return next
}